import { useState } from "react";
import { api } from "@/lib/api";
import { runGated } from "@/lib/adsGate";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Droplet, Sparkles, Loader2, FlaskConical } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useLatestBeautyScan } from "./useLatestBeautyScan";
import { ScanRequiredEmpty, ShopRecommendedButton } from "./beautyUi";

const FOCUS_OPTIONS = [
  { id: "all",   label: "Everything" },
  { id: "skin",  label: "Skin" },
  { id: "hair",  label: "Hair" },
  { id: "scalp", label: "Scalp" },
  { id: "beard", label: "Beard" },
];

export default function BeautyCare() {
  const { scan, loading } = useLatestBeautyScan();
  const [focus, setFocus] = useState("all");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [ingQuery, setIngQuery] = useState("");
  const [ingResult, setIngResult] = useState(null);
  const [ingBusy, setIngBusy] = useState(false);

  const run = async () => {
    setBusy(true); setResult(null);
    try {
      const { data } = await runGated("care_gen", () => api.post("/beauty/care/routine", { focus, scan_id: scan?.id }));
      setResult(data.result);
    } catch (e) {
      toast.error(String(e?.response?.data?.detail || "Couldn't build the routine."));
    } finally { setBusy(false); }
  };

  const lookup = async () => {
    if (!ingQuery.trim()) return;
    setIngBusy(true); setIngResult(null);
    try {
      const { data } = await api.post("/beauty/ingredient", { query: ingQuery.trim() });
      setIngResult(data.result);
    } catch (e) {
      toast.error("Ingredient lookup failed.");
    } finally { setIngBusy(false); }
  };

  if (loading) return <div className="max-w-5xl mx-auto p-16 text-center"><Loader2 className="mx-auto animate-spin" /></div>;
  if (!scan) return <div className="max-w-5xl mx-auto px-6 lg:px-10 py-10"><ScanRequiredEmpty /></div>;

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-10 lg:py-14" data-testid="beauty-care-page">
      <p className="eyebrow flex items-center gap-2"><Droplet size={12} /> Stylebay · Care Point</p>
      <h1 className="editorial-h1 mt-3">Care, <em>engineered for your scan.</em></h1>
      <p className="text-sm text-muted-foreground mt-3 max-w-2xl">
        A honest routine — skin, hair, scalp and beard — tuned to what the AI actually found on your face.
      </p>

      <div className="mt-8 grid lg:grid-cols-[1fr,1.4fr] gap-10">
        <section className="border border-border p-6 lg:p-8 space-y-6 self-start" data-testid="beauty-care-form">
          <div>
            <p className="eyebrow mb-3">Focus area</p>
            <div className="grid grid-cols-2 gap-2">
              {FOCUS_OPTIONS.map(f => (
                <button
                  key={f.id}
                  type="button"
                  data-testid={`beauty-care-focus-${f.id}`}
                  onClick={() => setFocus(f.id)}
                  className={`h-10 text-[10px] uppercase tracking-[0.18em] border ${focus === f.id ? "bg-accent text-accent-foreground border-foreground" : "border-border hover:border-foreground"}`}
                >{f.label}</button>
              ))}
            </div>
          </div>
          <Button disabled={busy} onClick={run} className="w-full" data-testid="beauty-care-generate">
            {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Building your routine…</> : <><Sparkles className="w-4 h-4 mr-2" /> Build my routine</>}
          </Button>

          <div className="pt-6 border-t border-border">
            <p className="eyebrow flex items-center gap-2 mb-3"><FlaskConical size={12} /> Ingredient intelligence</p>
            <p className="text-xs text-muted-foreground mb-3">Paste an ingredient list or a single actives (e.g. &ldquo;Niacinamide 10%&rdquo;).</p>
            <div className="flex gap-2">
              <Input
                value={ingQuery}
                onChange={(e) => setIngQuery(e.target.value)}
                placeholder="e.g. Salicylic acid"
                className="rounded-none h-10"
                data-testid="beauty-care-ingredient-input"
              />
              <Button onClick={lookup} disabled={ingBusy || !ingQuery.trim()} data-testid="beauty-care-ingredient-lookup" className="rounded-none h-10 text-[11px] uppercase tracking-[0.18em]">
                {ingBusy ? <Loader2 size={14} className="animate-spin" /> : "Lookup"}
              </Button>
            </div>
            {ingResult && <IngredientCard data={ingResult} />}
          </div>
        </section>

        <section data-testid="beauty-care-result">
          {!result && !busy && (
            <div className="border border-dashed border-border p-10 text-center">
              <Droplet size={26} strokeWidth={1.3} className="mx-auto opacity-60" />
              <p className="font-serif text-xl mt-4">Your routine appears here.</p>
              <p className="text-xs text-muted-foreground mt-2 max-w-sm mx-auto">
                Choose a focus and tap &ldquo;Build my routine&rdquo; — morning, evening, weekly steps + ingredient guidance.
              </p>
            </div>
          )}
          {result && <CareOutput data={result} beardApplicable={scan?.analysis?.beard?.applicable} />}
        </section>
      </div>
    </div>
  );
}

function CareOutput({ data, beardApplicable }) {
  const { summary, top_priorities = [], skin = {}, hair = {}, scalp = {}, beard = {}, reminders = [], shop_categories = [] } = data;
  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-10">
      {summary && <p className="font-serif text-2xl italic leading-snug">&ldquo;{summary}&rdquo;</p>}

      {top_priorities.length > 0 && (
        <div>
          <p className="eyebrow mb-3">Focus first</p>
          <ol className="space-y-2">
            {top_priorities.map((p, i) => (
              <li key={i} className="border-l-2 border-accent pl-4 py-1.5">
                <p className="text-sm font-medium">{i + 1}. {p.concern}</p>
                {p.why_it_matters && <p className="text-xs text-muted-foreground mt-1">{p.why_it_matters}</p>}
                {p.expected_timeline && <p className="text-xs text-accent mt-1">Timeline · {p.expected_timeline}</p>}
              </li>
            ))}
          </ol>
        </div>
      )}

      {skin.morning?.length > 0 && <RoutineSection title="Morning skincare" items={skin.morning} />}
      {skin.evening?.length > 0 && <RoutineSection title="Evening skincare" items={skin.evening} />}
      {skin.weekly?.length > 0  && <RoutineSection title="Weekly rituals"   items={skin.weekly} />}

      {(skin.ingredients_to_seek?.length || skin.ingredients_to_avoid?.length) && (
        <div className="grid sm:grid-cols-2 gap-6">
          <IngList label="Seek" items={skin.ingredients_to_seek} tone="positive" />
          <IngList label="Avoid" items={skin.ingredients_to_avoid} tone="negative" />
        </div>
      )}

      {(hair.routine?.length > 0) && (
        <div>
          <p className="eyebrow mb-3">Hair routine</p>
          <div className="text-xs text-muted-foreground mb-3 flex flex-wrap gap-x-6 gap-y-1">
            {hair.wash_frequency && <span>Wash · {hair.wash_frequency}</span>}
            {hair.oil_frequency && <span>Oil · {hair.oil_frequency}</span>}
            {hair.deep_conditioning && <span>Deep condition · {hair.deep_conditioning}</span>}
          </div>
          <RoutineList items={hair.routine} />
        </div>
      )}

      {scalp.routine?.length > 0 && (
        <div>
          <p className="eyebrow mb-3">Scalp care</p>
          {scalp.notes && <p className="text-xs text-muted-foreground mb-3">{scalp.notes}</p>}
          <RoutineList items={scalp.routine} />
        </div>
      )}

      {beardApplicable && beard.routine?.length > 0 && (
        <div>
          <p className="eyebrow mb-3">Beard care</p>
          <RoutineList items={beard.routine} />
        </div>
      )}

      {reminders.length > 0 && (
        <div>
          <p className="eyebrow mb-3">Reminders</p>
          <ul className="space-y-2 text-sm">
            {reminders.map((r, i) => (
              <li key={i} className="flex items-baseline gap-2">
                <span className="w-1.5 h-1.5 bg-accent" />
                <span><strong>{r.type}</strong> · {r.cadence}{r.time_of_day ? ` · ${r.time_of_day}` : ""}{r.note ? ` — ${r.note}` : ""}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {shop_categories.length > 0 && (
        <div className="border border-border p-5">
          <p className="eyebrow mb-3">Shop for your routine</p>
          <div className="flex flex-wrap gap-2">
            {shop_categories.map((c, i) => (
              <ShopRecommendedButton
                key={i}
                category={c.category_slug || c.label?.toLowerCase().replace(/\s+/g, "_") || "acne"}
                concerns={c.concerns}
                label={c.label || "Shop products"}
                testId={`beauty-care-shop-${i}`}
              />
            ))}
          </div>
        </div>
      )}
    </motion.div>
  );
}

function RoutineSection({ title, items }) {
  return (
    <div>
      <p className="eyebrow mb-3">{title}</p>
      <RoutineList items={items} />
    </div>
  );
}

function RoutineList({ items = [] }) {
  return (
    <ol className="space-y-2">
      {items.map((it, i) => (
        <li key={i} className="border-l-2 border-border pl-4 py-1.5">
          <p className="text-sm"><span className="eyebrow mr-2">{String(it.step || i + 1).padStart(2, "0")}</span><strong>{it.product_category || it.product_type}</strong></p>
          {it.purpose && <p className="text-xs text-muted-foreground mt-1">{it.purpose}</p>}
          {(it.key_ingredients?.length > 0) && (
            <p className="text-xs text-accent mt-1">Actives · {it.key_ingredients.join(", ")}</p>
          )}
          {it.shop_category && (
            <div className="mt-2">
              <ShopRecommendedButton category={it.shop_category} label="Shop this step" testId={`care-step-shop-${i}`} />
            </div>
          )}
        </li>
      ))}
    </ol>
  );
}

function IngList({ label, items, tone }) {
  if (!items?.length) return null;
  const colour = tone === "positive" ? "text-foreground" : "text-muted-foreground";
  return (
    <div>
      <p className="eyebrow mb-2">{label}</p>
      <ul className={`text-sm space-y-1 ${colour} list-disc list-inside`}>{items.map((s, i) => <li key={i}>{s}</li>)}</ul>
    </div>
  );
}

function IngredientCard({ data }) {
  const verdictClass = ({
    great: "text-green-600",
    okay: "text-foreground",
    caution: "text-amber-600",
    avoid: "text-destructive",
  })[data?.suitability?.verdict] || "text-foreground";
  return (
    <div className="mt-4 border border-border p-4 space-y-2 text-sm" data-testid="beauty-care-ingredient-result">
      <p className="font-serif text-lg">{data.ingredient || "Ingredient"}</p>
      {data.purpose && <p className="text-xs text-muted-foreground">{data.purpose}</p>}
      {data.suitability?.verdict && (
        <p className={`text-xs uppercase tracking-[0.2em] ${verdictClass}`}>
          {data.suitability.verdict} — {data.suitability.reason || ""}
        </p>
      )}
      {data.benefits?.length > 0 && (
        <div>
          <p className="eyebrow mt-2 mb-1">Benefits</p>
          <ul className="list-disc list-inside space-y-0.5 text-xs">{data.benefits.map((b, i) => <li key={i}>{b}</li>)}</ul>
        </div>
      )}
      {data.potential_irritants?.length > 0 && (
        <p className="text-xs text-destructive">Watch out · {data.potential_irritants.join(", ")}</p>
      )}
      {data.stacking_advice?.length > 0 && (
        <p className="text-xs text-accent">Pairs well · {data.stacking_advice.join(" · ")}</p>
      )}
    </div>
  );
}
